<glxy-page>
  <glxy-page-toolbar>
    <glxy-page-nav>
      <glxy-page-nav-button useRelativePath="true" previousPageUrl="../../../"></glxy-page-nav-button>
    </glxy-page-nav>
    <glxy-page-title>
      <span>{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.TITLE' | translate }}</span>
    </glxy-page-title>
  </glxy-page-toolbar>

  <glxy-page-wrapper widthPreset="full">
    @if (isLoading()) {
      <div class="loading-container">
        <glxy-loading-spinner></glxy-loading-spinner>
      </div>
    } @else {
      <div class="comparison-header">
        <div class="run-info run-a">
          <h3>{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.RUN_A' | translate }}</h3>
          @if (runA()?.created) {
            <span class="run-date">{{ runA()?.created | date: 'medium' }}</span>
          }
        </div>
        <div class="vs">
          <mat-icon>compare_arrows</mat-icon>
        </div>
        <div class="run-info run-b">
          <h3>{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.RUN_B' | translate }}</h3>
          @if (runB()?.created) {
            <span class="run-date">{{ runB()?.created | date: 'medium' }}</span>
          }
        </div>
      </div>

      <mat-card class="summary-card">
        <mat-card-header>
          <mat-card-title>{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.SUMMARY' | translate }}</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <div class="summary-stats">
            <div class="stat">
              <span class="stat-value">{{ summary().total }}</span>
              <span class="stat-label">{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.COMPARED' | translate }}</span>
            </div>
            <div class="stat positive">
              <span class="stat-value">{{ summary().improved }}</span>
              <span class="stat-label">{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.IMPROVED' | translate }}</span>
            </div>
            <div class="stat negative">
              <span class="stat-value">{{ summary().regressed }}</span>
              <span class="stat-label">{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.REGRESSED' | translate }}</span>
            </div>
            <div class="stat neutral">
              <span class="stat-value">{{ summary().unchanged }}</span>
              <span class="stat-label">{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.UNCHANGED' | translate }}</span>
            </div>
            @if (summary().onlyInA > 0 || summary().onlyInB > 0) {
              <div class="stat">
                <span class="stat-value">{{ summary().onlyInA + summary().onlyInB }}</span>
                <span class="stat-label">{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.UNMATCHED' | translate }}</span>
              </div>
            }
          </div>
        </mat-card-content>
      </mat-card>

      @if (comparedCases().length > 0) {
        <mat-card class="comparison-card">
          <mat-card-header>
            <mat-card-title>{{
              'AI_ASSISTANT.TESTING.RUN_COMPARISON.TEST_CASE_COMPARISON' | translate
            }}</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            <mat-accordion>
              @for (comparison of comparedCases(); track comparison.testCaseId) {
                <mat-expansion-panel>
                  <mat-expansion-panel-header>
                    <mat-panel-title>
                      <div class="panel-title">
                        <span class="test-name">{{ comparison.name }}</span>
                        <div class="status-icons">
                          <mat-icon
                            [ngClass]="statusMap.get(comparison.runAResult?.status || '')?.class"
                            [matTooltip]="
                              ('AI_ASSISTANT.TESTING.RUN_COMPARISON.RUN_A' | translate) +
                              ': ' +
                              (comparison.runAResult?.status || 'N/A')
                            "
                          >
                            {{ statusMap.get(comparison.runAResult?.status || '')?.icon || 'help' }}
                          </mat-icon>
                          <mat-icon>arrow_forward</mat-icon>
                          <mat-icon
                            [ngClass]="statusMap.get(comparison.runBResult?.status || '')?.class"
                            [matTooltip]="
                              ('AI_ASSISTANT.TESTING.RUN_COMPARISON.RUN_B' | translate) +
                              ': ' +
                              (comparison.runBResult?.status || 'N/A')
                            "
                          >
                            {{ statusMap.get(comparison.runBResult?.status || '')?.icon || 'help' }}
                          </mat-icon>
                        </div>
                      </div>
                    </mat-panel-title>
                    <mat-panel-description>
                      <span class="score-delta" [ngClass]="getDeltaClass(comparison.scoreDelta)">
                        {{ formatDelta(comparison.scoreDelta) }}
                      </span>
                    </mat-panel-description>
                  </mat-expansion-panel-header>

                  <div class="side-by-side">
                    <div class="run-details run-a-details">
                      <h4>{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.RUN_A' | translate }}</h4>
                      @if (comparison.runAResult) {
                        <ng-container
                          *ngTemplateOutlet="testResultDetails; context: { $implicit: comparison.runAResult }"
                        ></ng-container>
                      } @else {
                        <p class="no-data">{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.NOT_IN_RUN_A' | translate }}</p>
                      }
                    </div>

                    <div class="run-details run-b-details">
                      <h4>{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.RUN_B' | translate }}</h4>
                      @if (comparison.runBResult) {
                        <ng-container
                          *ngTemplateOutlet="testResultDetails; context: { $implicit: comparison.runBResult }"
                        ></ng-container>
                      } @else {
                        <p class="no-data">{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.NOT_IN_RUN_B' | translate }}</p>
                      }
                    </div>
                  </div>
                </mat-expansion-panel>
              }
            </mat-accordion>
          </mat-card-content>
        </mat-card>
      }

      @if (unmatchedRunA().length > 0 || unmatchedRunB().length > 0) {
        <mat-card class="unmatched-card">
          <mat-card-header>
            <mat-card-title>{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.UNMATCHED_TESTS' | translate }}</mat-card-title>
          </mat-card-header>
          <mat-card-content>
            @if (unmatchedRunA().length > 0) {
              <div class="unmatched-section">
                <h4>
                  {{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.ONLY_IN_RUN_A' | translate }} ({{ unmatchedRunA().length }})
                </h4>
                <ng-container
                  *ngTemplateOutlet="unmatchedTestList; context: { $implicit: unmatchedRunA() }"
                ></ng-container>
              </div>
            }
            @if (unmatchedRunB().length > 0) {
              <div class="unmatched-section">
                <h4>
                  {{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.ONLY_IN_RUN_B' | translate }} ({{ unmatchedRunB().length }})
                </h4>
                <ng-container
                  *ngTemplateOutlet="unmatchedTestList; context: { $implicit: unmatchedRunB() }"
                ></ng-container>
              </div>
            }
          </mat-card-content>
        </mat-card>
      }
    }
  </glxy-page-wrapper>
</glxy-page>

<ng-template #unmatchedTestList let-results>
  <mat-accordion>
    @for (result of results; track result.id) {
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title>
            <div class="panel-title">
              <mat-icon [ngClass]="statusMap.get(result.status)?.class">
                {{ statusMap.get(result.status)?.icon || 'help' }}
              </mat-icon>
              <span class="test-name">{{ result.name }}</span>
            </div>
          </mat-panel-title>
          <mat-panel-description>
            <span class="score-display"
              >{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.SCORE' | translate }} {{ formatScore(result.score) }}</span
            >
          </mat-panel-description>
        </mat-expansion-panel-header>

        <div class="unmatched-result-details">
          <ng-container *ngTemplateOutlet="testResultDetails; context: { $implicit: result }"></ng-container>
        </div>
      </mat-expansion-panel>
    }
  </mat-accordion>
</ng-template>

<ng-template #testResultDetails let-result>
  <div class="detail-row">
    <span class="label">{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.STATUS' | translate }}</span>
    <span [ngClass]="statusMap.get(result.status)?.class">{{ result.status }}</span>
  </div>
  <div class="detail-row">
    <span class="label">{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.SCORE' | translate }}</span>
    <span>{{ formatScore(result.score) }}</span>
  </div>
  @if (!isVoice(result)) {
    <div class="detail-row">
      <span class="label">{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.MODEL' | translate }}</span>
      <span>{{ getModelAndReasoning(result) }}</span>
    </div>
    <div class="detail-row">
      <span class="label">{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.LATENCY' | translate }}</span>
      <span>{{ result.latency?.toFixed(2) || 'N/A' }}s</span>
    </div>
  } @else {
    <div class="detail-row">
      <span class="label">{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.CALL_DURATION' | translate }}</span>
      <span>{{ getFormattedCallDuration(result.callDuration) }}</span>
    </div>
  }
  @if (isVoice(result)) {
    @if (result.callerInstructions) {
      <div class="detail-section">
        <span class="label">{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.CALLER_INSTRUCTIONS' | translate }}</span>
        <div class="response-text">{{ result.callerInstructions }}</div>
      </div>
    }
    @if (result.recordingUrl) {
      <div class="detail-section">
        <span class="label">{{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.RECORDING' | translate }}</span>
        <audio controls preload="metadata" [src]="result.recordingUrl"></audio>
      </div>
    }
  }
  @if (result.chatHistory?.length) {
    <div class="detail-section">
      <span class="label">
        @if (isVoice(result)) {
          {{ 'AI_ASSISTANT.TESTING.RESULTS_MODAL.TRANSCRIPT' | translate }}
        } @else {
          {{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.CHAT_HISTORY' | translate }}
        }
      </span>
      <ai-test-chat-history
        [messages]="result.chatHistory"
        [testType]="result.testType"
        [compact]="true"
      ></ai-test-chat-history>
    </div>
  }
  @if (result.expected) {
    <div class="detail-section">
      <span class="label">{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.EXPECTATION' | translate }}</span>
      <div class="response-text">{{ result.expected }}</div>
    </div>
  }
  @if (result.evaluation) {
    <div class="detail-section">
      <span class="label">{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.EVALUATION' | translate }}</span>
      <div class="response-text">{{ result.evaluation }}</div>
    </div>
  }
  @if (result.reasoning) {
    <div class="detail-section">
      <span class="label">{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.REASONING_LABEL' | translate }}</span>
      <div class="reasoning-text">{{ result.reasoning }}</div>
    </div>
  }
  @if (result.toolCalls?.length) {
    <div class="detail-section">
      <span class="label">{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.TOOLS_CALLED' | translate }}</span>
      <div class="tool-calls-list">
        @for (tool of result.toolCalls; track tool) {
          <div class="function">
            <a (click)="openToolCallCitationDetails(tool.toolName, tool.arguments, tool.response)">
              {{ tool.toolName }}
              <mat-icon>offline_bolt</mat-icon>
            </a>
          </div>
        }
      </div>
    </div>
  }
  @if (result.citations?.length) {
    <div class="detail-section">
      <span class="label">{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.SOURCES' | translate }}</span>
      <div>
        @for (citation of result.citations; track citation) {
          @if (citation.link) {
            <span>{{ citation.title }} - </span>
            <a [href]="citation.link" target="_blank"
              >{{ citation.link }}
              <mat-icon inline="true">open_in_new</mat-icon>
            </a>
          }
          @if (citation.fileUrl) {
            <span>{{ citation.title }} - </span>
            <a [href]="citation.fileUrl" target="_blank"
              >{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.VIEW_FILE' | translate }}
              <mat-icon inline="true">open_in_new</mat-icon>
            </a>
          }
          <div>{{ citation.reason }}</div>
        }
      </div>
    </div>
  }
  @if (result.chunks?.length) {
    <div class="detail-section">
      <span class="label">{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.STREAMING_CHUNKS' | translate }}</span>
      <ul class="chunk-list">
        @for (chunk of result.chunks; track chunk.sequenceNumber) {
          <li>
            <div class="chunk-item" [ngClass]="{ 'error-chunk': chunk.isError }">
              <div class="chunk-header">
                @if (chunk.isError) {
                  <span class="chunk-type error">{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.ERROR' | translate }}</span>
                } @else {
                  <span class="chunk-type">{{ getContentTypeName(chunk.contentType) }}</span>
                }
                <span class="chunk-sequence"
                  >{{ 'AI_ASSISTANT.TESTING.RUN_COMPARISON.SEQUENCE' | translate }} {{ chunk.sequenceNumber }}</span
                >
                @if (chunk.timestamp) {
                  <span class="chunk-timestamp">{{ chunk.timestamp | date: 'short' }}</span>
                }
              </div>
              @if (chunk.isError) {
                <div class="chunk-content error">{{ chunk.errorMessage }}</div>
              } @else {
                <div class="chunk-content">{{ chunk.content }}</div>
              }
            </div>
          </li>
        }
      </ul>
    </div>
  }
</ng-template>
